import { latLonToEN, pointsToEN } from '../ol-utils';
import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import Feature, { FeatureLike } from 'ol/Feature';

import { LineString, Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';
import { DxfService } from '@openlayers/DxfService';


/**
 * @author NikolaV
 */

const FEATURE_TYPE = 'pipe';

const sharedStyles = {
    hitArea: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.005],
            width: 15,
        }),
    })
};

const stylesCache = {};

function styleFunction(feature: FeatureLike) {
    const final = [sharedStyles.hitArea];
    const data: IPipeNetwork = feature.get('data');

    const isRect = data.props?.type === "RectStruct"
    const styles = createStyleForData(data);

    if (feature.get('hover')) {
        final.push(styles.lineHighlight, isRect ? styles.squareHighlight : styles.circleHighlight);
    } else {
        final.push(styles.line, isRect ? styles.square : styles.circle);
    }
    return final;
}

function createStyleForData(data: IPipeNetwork) {
    return createStyle(DxfService.getColorFromEntity(data));
}

function createStyle(rgbStr: string): {
    line: olStyle.Style, lineHighlight: olStyle.Style, circle: olStyle.Style, circleHighlight: olStyle.Style, square: olStyle.Style, squareHighlight: olStyle.Style
} {
    rgbStr = rgbStr || '0,0,0';
    const rgb: any[] = rgbStr.split(',');
    if (rgb.length != 3) rgbStr = '0,0,0';

    if (stylesCache[rgbStr]) return stylesCache[rgbStr];

    const styles = {
        line: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [rgb[0], rgb[1], rgb[2], 0.8],
                width: 1.2,
            }),
        }),

        circle: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 5,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 1.2,
                }),
            }),
        }),

        square: new olStyle.Style({
            image: new olStyle.RegularShape({
                radius: 6,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 1.2,
                }),
                points: 4,
                angle: Math.PI / 4,
            }),
        }),

        lineHighlight: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [rgb[0], rgb[1], rgb[2], 0.8],
                width: 4,
            }),
        }),

        circleHighlight: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 5,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 3,
                }),
            }),
        }),

        squareHighlight: new olStyle.Style({
            image: new olStyle.RegularShape({
                radius: 6,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 3,
                }),
                points: 4,
                angle: Math.PI / 4,
            }),
        })
    };

    stylesCache[rgbStr] = styles;
    return styles;
}

function makeFeatures(network: IPipeNetwork, mouseInteraction?: FeatureMouseInteraction): Feature[] {
    const features: Feature[] = [];

    const source: FeatureSource = {
        modelId: network.modelId,
    };

    for (const layer of network.layers) {
        // each layer has array of polylines and points

        for (const polyline of layer.polylines) {
            const coords: Coordinate[] = pointsToEN(polyline.points);
            if (!coords) {
                console.warn('Skipping bad pipe network: ', network, polyline);
                return null;
            }

            const feature = new Feature({
                geometry: new LineString(coords),
            });

            feature.set('source', source);
            feature.set('data', polyline);
            feature.set('props', polyline.props);
            feature.set('type', FEATURE_TYPE);
            feature.set('mouseInteraction', mouseInteraction);
            features.push(feature);
        }

        for (const point of layer.points) {
            const coord = latLonToEN(point.lat, point.lon);

            if (coord) {
                let t = '';
                let d = '';
                let w = '';
                let l = '';

                if (typeof (point.props) !== 'undefined' && point.props !== null) {
                    if (typeof (point.props.type) !== 'undefined' && point.props.type !== null) {
                        t = point.props.type;
                    }
                    if (typeof (point.props.diameter) !== 'undefined' && point.props.diameter !== null) {
                        d = point.props.diameter;
                    }
                    if (typeof (point.props.width) !== 'undefined' && point.props.width !== null) {
                        w = point.props.width;
                    }
                    if (typeof (point.props.length) !== 'undefined' && point.props.length !== null) {
                        l = point.props.length;
                    }
                }

                if ((t === 'CircStruct' && Number(d) !== 0) ||
                    (t === 'RectStruct' && Number(l) !== 0 && Number(w) !== 0))
                {
                    const feature = new Feature({
                        geometry: new OlPoint(coord),
                    });
                    feature.set('source', source);
                    feature.set('data', point);
                    feature.set('props', point.props);
                    feature.set('type', FEATURE_TYPE);
                    feature.set('mouseInteraction', mouseInteraction);
                    features.push(feature);
                }
            }
        }
    }

    return features;
}

function makeArrowFeatures(sourceFeature: Feature, position: 'end' | 'start' | 'center' = 'end'): Feature[] {
    const features = [];
    const data = sourceFeature.get('data');
    const geometry = sourceFeature.getGeometry() as LineString;

    if (geometry.forEachSegment && data.points?.length > 1 &&
        data.points[0].elevation && data.points[1].elevation &&
        (data.points[0].elevation !== data.points[1].elevation) &&
        (data.points[0].lat !== data.points[1].lat) && (data.points[0].lon !== data.points[1].lon)
    ) {
        geometry.forEachSegment((start, end) => {
            if (data.points[0].elevation < data.points[1].elevation) {
                [start, end] = [end, start];
            }
            const dx = end[0] - start[0];
            const dy = end[1] - start[1];
            const rotation = Math.atan2(dy, dx);

            let point = end;
            if (position === 'start') {
                point = start;
            }
            else if (position === 'center') {
                point = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
            }

            const arrowFeature = new Feature({
                geometry: new OlPoint(point),
            });
            arrowFeature.set('rotation', -rotation+1.57);
            arrowFeature.set('offset', position === 'end' ? [0, -15] : [0, 0]);
            arrowFeature.set('source', sourceFeature.get('source'));
            features.push(arrowFeature);
        });
    }
    return features;
}

export const PipeFeatureFactory = {
    makeFeatures,
    makeArrowFeatures,
    styleFunction,
    FEATURE_TYPE,
};
